import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import { fetchScans, selectActivity } from '../../reducers/activity'
import { useSelector } from 'react-redux'
import DataGrid from '../data-grid/DataGrid'
import { CrossCircledIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons'
import checkIcon from '../../assets/check_circle.svg'
import Popout from '../popout/Popout'
import { DEFAULT_PAGE, MAXIMUM_PAGE_SIZE } from '../../strings/api-consts'
import { IconButton, TextField } from '@radix-ui/themes'
import { generateAndDownloadReport } from '../../reducers/alerts'
import PageSkeleton from '../skeleton/PageSkeleton'
import { Pagination } from '../pagination/Pagination'
import { Activity, ActivityPopoutRequest } from '../../types/ActivityTypes'
import { activityColumns } from '../activity/ActivityColumns'
import ThreatHunterSettings from '../settings/ThreatHunter'
import { selectUser } from '../../reducers/users'
import { PaginationSelectOption } from '../../types/MemberTypes'
import { inPov } from '../../utils/session'
import { HEADER_HEIGHT_WITH_TRIAL, HEADER_HEIGHT_WITHOUT_TRIAL } from '../../strings/consts'

function ThreatHunterWrapper() {
  const di = useAppDispatch()
  const { scans, totalPages, activityTableCurrentPage, threatHunterScreen } = useSelector(selectActivity)
  const { session } = useSelector(selectUser)
  const isDemo = session?.user?.email === 'demo@acme-inc.io'

  const [showPopout, setShowPopout] = useState<boolean>(false)
  const [dataLoading, setDataLoading] = useState<boolean>(true)
  const [popoutData, setPopoutData] = useState<ActivityPopoutRequest | undefined>(undefined)
  const [contentScreen, setContentScreen] = useState<string>('EDR')
  const [nameFilter, setNameFilter] = useState<string>('')

  useEffect(() => {
    setDataLoading(true)
    di(fetchScans(DEFAULT_PAGE, MAXIMUM_PAGE_SIZE, [], nameFilter, 'threat_hunter'))
  }, [])

  useEffect(() => {
    if (scans || (scans && (scans as Activity[]).length === 0)) {
      setDataLoading(false)
    } else if (!scans) {
      setDataLoading(true)
    }
  }, [scans])

  const handlePageClick = (data: PaginationSelectOption) => {
    setDataLoading(true)
    di(fetchScans(data.selected + 1, MAXIMUM_PAGE_SIZE, [], nameFilter, 'threat_hunter'))
  }

  const getScanDetails = async (scanId: string, scanType: string) => {
    const scanDetails = {
      scanId: scanId,
      scanType: scanType,
    }
    setContentScreen('DEEP')
    setPopoutData(scanDetails)
    setShowPopout(true)
  }

  const searchName = () => {
    setDataLoading(true)
    di(fetchScans(DEFAULT_PAGE, MAXIMUM_PAGE_SIZE, [], nameFilter, 'threat_hunter'))
  }

  const clearNameFilter = () => {
    setDataLoading(true)
    di(fetchScans(DEFAULT_PAGE, MAXIMUM_PAGE_SIZE, [], '', 'threat_hunter'))
  }

  const downloadAlertReport = async (scanId: string, isThreatHunter: boolean) => {
    await di(generateAndDownloadReport(scanId, isThreatHunter, isDemo))
  }

  return (
    <>
      <div
        className="absolute top-18 left-52 rounded-tl-lg border-mainBorder border-t border-l bg-contentBackground w-[calc(100%_-_208px)] p-9"
        style={{
          height: inPov(session)
            ? `calc(100% - ${HEADER_HEIGHT_WITH_TRIAL})`
            : `calc(100% - ${HEADER_HEIGHT_WITHOUT_TRIAL})`,
        }}
      >
        {threatHunterScreen === 'scans' && (
          <>
            {!dataLoading && (
              <div className="flex justify-between">
                <div className="mb-6 flex w-full">
                  <TextField.Root
                    value={nameFilter}
                    placeholder="Search..."
                    className="w-[200px] h-[38px] mr-4 bg-inputBackground text-mainText dark:shadow-none border border-transparent dark:border-neutral-11 hover:shadow-none hover:border hover:border-borderPurple hover:dark:border-borderPurple focus:ring-mainBorder focus:ring-borderPurple p-0"
                    onChange={(e) => {
                      setNameFilter(e.target.value)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        searchName()
                      }
                    }}
                  >
                    <TextField.Slot className="p-0"></TextField.Slot>
                    {nameFilter !== '' && (
                      <TextField.Slot>
                        <IconButton
                          size="1"
                          variant="ghost"
                          onClick={() => {
                            setNameFilter('')
                            clearNameFilter()
                          }}
                        >
                          <CrossCircledIcon height="14" width="14" className="cursor-pointer dark:text-white" />
                        </IconButton>
                      </TextField.Slot>
                    )}
                    <TextField.Slot>
                      <IconButton size="1" variant="ghost" className="dark:text-white" onClick={() => searchName()}>
                        <MagnifyingGlassIcon height="14" width="14" />
                      </IconButton>
                    </TextField.Slot>
                  </TextField.Root>
                </div>
              </div>
            )}

            {(!scans || dataLoading) && (
              <div className="flex justify-center items-center min-h-screen">
                <PageSkeleton />
              </div>
            )}
            {scans && scans.length > 0 && (
              <>
                <DataGrid
                  columns={activityColumns(getScanDetails, downloadAlertReport, 'threat_hunter')}
                  data={scans}
                  loading={dataLoading}
                  classNames={inPov(session) ? 'max-h-[calc(100vh_-_276px)]' : 'max-h-[calc(100vh_-_240px)]'}
                />
                <div className="mt-2">
                  <Pagination
                    totalPages={totalPages}
                    forcePage={activityTableCurrentPage}
                    handlePageClick={handlePageClick}
                  />
                </div>
              </>
            )}
            {scans && scans.length === 0 && (
              <div className="flex items-center justify-center h-screen">
                <div className="text-center -mt-32">
                  <div className="flex items-center justify-center">
                    <img src={checkIcon} alt="No open alerts found" className="h-12 w-12" />
                  </div>
                  <p className="font-bold">No Activity</p>
                  <p className="text-sm">You have no Threat Hunter activity to view</p>
                </div>
              </div>
            )}
          </>
        )}
        {threatHunterScreen === 'settings' && <ThreatHunterSettings />}
      </div>
      {showPopout && <Popout setShowPopout={setShowPopout} contentScreen={contentScreen} popoutData={popoutData} />}
    </>
  )
}

export default ThreatHunterWrapper
